import CallIcon from "@mui/icons-material/Call";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Grid,
    InputAdornment,
    TextField,
    Typography,
} from "@mui/material";
import { Formik } from "formik";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { loginByCode } from "../../../application/actions/authActions";
import { getAuthSlice } from "../../../application/selectors/auth";
import {
    getMessageSeverity,
    getMessageSlice,
    getMessageText,
} from "../../../application/selectors/message";
import { clearMessage, setMessage } from "../../../application/slices/message";
import DefaultApiService from "../../../infrastructure/api/default";
import { resolvePostLoginUrl, readStoredUser } from "../../../infrastructure/utils/authSession";
import { normalizeDigits } from "../../../infrastructure/utils/formatters";
import {
  attachReferralRegistrationIfNeeded,
  hasActiveReferral,
} from "../../../infrastructure/utils/referralTracking";
import { attachContractorReferralRegistrationIfNeeded } from "../../../infrastructure/utils/contractorReferralTracking";
import Captcha from "../shared/Captcha";
import OtpCodeInput from "../shared/OtpCodeInput";
import PanelInfo from "../shared/panel-info";
import VpnLoginHint from "./VpnLoginHint";

const validationSchema = Yup.object().shape({
  mobileNumber: Yup.string().required("لطفا این فیلد را پر کنید."),
  sentVerifyCode: Yup.boolean(),
  captcha: Yup.string().when("sentVerifyCode", {
    is: false,
    then: (schema) => schema.required("لطفا کد امنیتی را وارد کنید."),
    otherwise: (schema) => schema.notRequired(),
  }),
  verifyCode: Yup.string().when("sentVerifyCode", {
    is: true,
    then: (schema) =>
      schema
        .required("لطفا این فیلد را پر کنید.")
        .length(5, "کد دریافت شده شما 5 رقم می‌باشد."),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const MobileLogin = ({ hasBothLogin, onChangeLoginKind }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || "/";

  const [loading, setLoading] = useState(false);
  const [formValues, setFormValues] = useState({
    mobileNumber: "",
    verifyCode: "",
    captcha: "",
    sentVerifyCode: false,
  });

  const [timer, setTimer] = useState(null);
  const [verifyResendTime, setVerifyResendTime] = useState(0);
  const [canSendCode, setCanSendCode] = useState(true);
  const captchaRef = useRef(null);

  const { isLoggedIn } = useSelector(getAuthSlice);
  const { message } = useSelector(getMessageSlice);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(clearMessage());
  }, [dispatch]);

  useEffect(() => {
    if (verifyResendTime <= 0) {
      window.clearInterval(timer);
    }

    return () => {
      window.clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    if (verifyResendTime <= 0) {
      window.clearInterval(timer);
      setCanSendCode(true);
    }
    if (verifyResendTime === 60) {
      setCanSendCode(false);
      const t = window.setInterval(() => {
        setVerifyResendTime((prevTime) => prevTime - 1);
      }, 1000);
      setTimer(t);
    }
  }, [verifyResendTime]);

  const onSubmit = async (
    formValue,
    { setSubmitting, setFieldError, setValues }
  ) => {
    const { mobileNumber, verifyCode, captcha, sentVerifyCode } = formValue;

    console.log("Form submitted:", { mobileNumber, captcha, sentVerifyCode });

    // Sent Verify Code
    if (!sentVerifyCode) {
      setLoading(true);

      // Validate captcha
      const isValid = captchaRef.current?.validate();
      console.log("Captcha validation result:", isValid);

      if (!isValid) {
        setFieldError("captcha", "کد امنیتی اشتباه است");
        setLoading(false);
        setSubmitting(false);
        captchaRef.current?.refresh();
        return;
      }

      const sentCodeResult = await sendVerifyCode(mobileNumber);

      if (sentCodeResult?.isSuccess) {
        console.log("Code sent successfully, updating form state");

        // Update both formValues and Formik values
        const newValues = {
          mobileNumber,
          verifyCode: "",
          captcha: "",
          sentVerifyCode: true,
        };

        setFormValues(newValues);
        setValues(newValues);
      } else {
        console.log("Failed to send code");
        // Refresh captcha on error
        captchaRef.current?.refresh();
      }

      setSubmitting(false);
      setLoading(false);
    } else {
      setLoading(true);
      // normalize digits just before dispatch
      const mobileNorm = normalizeDigits(mobileNumber);
      const codeNorm = normalizeDigits(verifyCode);

      dispatch(loginByCode({ mobileNumber: mobileNorm, verifyCode: codeNorm }))
        .unwrap()
        .then(async ({ user }) => {
          await attachReferralRegistrationIfNeeded();
          await attachContractorReferralRegistrationIfNeeded();
          navigate(resolvePostLoginUrl(from, user), { replace: true });
        })
        .catch(() => {
          setLoading(false);
          setSubmitting(false);
        });
    }
  };

  const sendVerifyCode = async (mobileNumber) => {
    try {
      // بررسی محدودیت زمانی (1 دقیقه)
      if (!canSendCode) {
        dispatch(setMessage(`لطفاً ${verifyResendTime} ثانیه صبر کنید.`));
        return { isSuccess: false };
      }

      dispatch(clearMessage());

      const normalizedMobile = normalizeDigits(mobileNumber);

      const sentCodeResult = await DefaultApiService.postApi(
        "Account/VerifyRequest",
        {
          mobileNumber: normalizedMobile,
          verifyCode: 0,
        }
      );

      if (sentCodeResult?.isSuccess) {
        setVerifyResendTime(60);
      }

      return sentCodeResult;
    } catch (error) {
      dispatch(
        setMessage(error?.response?.data?.message || "خطایی رخ داده است.")
      );

      return { isSuccess: false };
    }
  };

  const storedUser = readStoredUser();
  if (isLoggedIn && storedUser?.accessToken) {
    return <Navigate to={resolvePostLoginUrl(from, storedUser)} replace />;
  }

  return (
    <Card variant="outlined" className="auth-card-container">
      <CardContent>
        <Formik
          initialValues={formValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
          enableReinitialize
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
            setFieldValue,
          }) => (
            <form onSubmit={handleSubmit}>
              <Grid container spacing={{ xs: 1.25, sm: 3 }}>
                <Grid item xs={12} md={12} className="auth-header">
                  <PanelInfo className="auth-logo" />

                  <div className="auth-title">
                    {hasActiveReferral()
                      ? "ورود یا ثبت‌نام با موبایل"
                      : "ورود به حساب کاربری"}
                  </div>
                  <VpnLoginHint />
                </Grid>

                <Grid
                  item
                  xs={12}
                  md={12}
                  sx={{ display: values.sentVerifyCode ? "none" : "block" }}
                >
                  <TextField
                    label="شماره همراه"
                    variant="outlined"
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CallIcon />
                        </InputAdornment>
                      ),
                    }}
                    name="mobileNumber"
                    type="string"
                    value={values.mobileNumber}
                    onChange={(e) =>
                      setFieldValue("mobileNumber", normalizeDigits(e.target.value))
                    }
                    error={errors.mobileNumber && touched.mobileNumber}
                    helperText={
                      errors.mobileNumber &&
                      touched.mobileNumber &&
                      errors.mobileNumber
                    }
                    disabled={values.sentVerifyCode}
                  />
                </Grid>

                <Grid
                  item
                  xs={12}
                  md={12}
                  className="auth-captcha-wrap"
                  sx={{ display: values.sentVerifyCode ? "none" : "block" }}
                >
                  <Captcha
                    ref={captchaRef}
                    value={values.captcha}
                    onChange={(val) => setFieldValue("captcha", val)}
                    error={errors.captcha && touched.captcha}
                    helperText={
                      errors.captcha && touched.captcha && errors.captcha
                    }
                  />
                </Grid>

                {values.sentVerifyCode ? (
                  <Grid item xs={12} md={12}>
                    <Typography
                      variant="h6"
                      textAlign="center"
                      sx={{
                        fontSize: { xs: "1rem", sm: "1.25rem" },
                        fontWeight: 600,
                        color: "primary.main",
                      }}
                    >
                      {values.mobileNumber}
                    </Typography>

                    <Typography
                      variant="body1"
                      textAlign="center"
                      sx={{
                        mt: 1,
                        fontSize: { xs: "0.875rem", sm: "1rem" },
                        color: "text.secondary",
                      }}
                    >
                      کد دریافتی را وارد کنید.
                    </Typography>
                  </Grid>
                ) : null}

                <Grid
                  item
                  xs={12}
                  md={12}
                  sx={{ display: values.sentVerifyCode ? "block" : "none" }}
                >
                  <Box
                    display="flex"
                    justifyContent="center"
                    mb={{ xs: 1, sm: 2 }}
                    className="code-input-container"
                    sx={{
                      width: "100%",
                      overflow: "hidden",
                    }}
                  >
                    <OtpCodeInput
                      type="number"
                      fields={5}
                      name="verifyCode"
                      value={values.verifyCode}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      style={{
                        direction: "ltr",
                        display: "flex",
                        justifyContent: "center",
                        gap: "4px",
                        flexWrap: "nowrap",
                      }}
                      inputStyle={{
                        width: "50px",
                        height: "50px",
                        fontSize: "18px",
                        textAlign: "center",
                        border: "2px solid #e0e0e0",
                        borderRadius: "50%",
                        outline: "none",
                        backgroundColor: "#ffffff",
                        color: "#333",
                        fontWeight: "500",
                        margin: "0 2px",
                        transition: "all 0.2s ease-in-out",
                        MozAppearance: "textfield",
                        WebkitAppearance: "none",
                        cursor: "text",
                      }}
                      inputStyleInvalid={{
                        width: "50px",
                        height: "50px",
                        fontSize: "18px",
                        textAlign: "center",
                        border: "2px solid #f44336",
                        borderRadius: "50%",
                        outline: "none",
                        backgroundColor: "#ffffff",
                        color: "#333",
                        fontWeight: "500",
                        margin: "0 2px",
                        transition: "all 0.2s ease-in-out",
                        MozAppearance: "textfield",
                        WebkitAppearance: "none",
                        cursor: "text",
                      }}
                      onChange={(value) => {
                        setFieldValue("verifyCode", normalizeDigits(value));
                      }}
                      isValid={!(errors.verifyCode && touched.verifyCode)}
                    />
                  </Box>

                  <p className="helper-text">
                    {errors.verifyCode &&
                      touched.verifyCode &&
                      errors.verifyCode}
                  </p>
                </Grid>

                {values.sentVerifyCode ? (
                  <Grid item xs={12} md={12}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        gap: { xs: 1, sm: 0.5 },
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {verifyResendTime > 0 ? (
                        <>
                          <Typography
                            variant="body1"
                            sx={{
                              minWidth: 60,
                              fontSize: { xs: "0.875rem", sm: "1rem" },
                            }}
                            fontWeight={"bold"}
                            color={"primary.main"}
                          >
                            {Math.floor(verifyResendTime / 60)}:
                            {(verifyResendTime % 60)
                              .toString()
                              .padStart(2, "0")}
                          </Typography>

                          <Typography
                            variant="body1"
                            color={"text.secondary"}
                            sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
                          >
                            تا ارسال کد بعدی
                          </Typography>
                        </>
                      ) : (
                        <>
                          <Button
                            variant="text"
                            size="large"
                            disabled={!canSendCode}
                            sx={{
                              fontSize: { xs: "0.75rem", sm: "0.875rem" },
                              padding: { xs: "6px 12px", sm: "8px 16px" },
                            }}
                            onClick={() => {
                              try {
                                if (canSendCode) {
                                  sendVerifyCode(values.mobileNumber);
                                  dispatch(clearMessage());
                                }
                              } catch (err) {
                                console.error("Error resending code:", err);
                                dispatch(setMessage("خطا در ارسال مجدد کد"));
                              }
                            }}
                          >
                            ارسال دوباره رمز عبور
                          </Button>

                          <Button
                            variant="text"
                            size="large"
                            color="navy"
                            sx={{
                              fontSize: { xs: "0.75rem", sm: "0.875rem" },
                              padding: { xs: "6px 12px", sm: "8px 16px" },
                            }}
                            onClick={() => {
                              setFieldValue("sentVerifyCode", false);
                            }}
                          >
                            تغییر شماره همراه
                          </Button>
                        </>
                      )}
                    </Box>
                  </Grid>
                ) : null}

                <Grid item xs={12} md={12}>
                  <Button
                    fullWidth
                    disabled={
                      isSubmitting || (!values.sentVerifyCode && !canSendCode)
                    }
                    variant="contained"
                    type="submit"
                    size="large"
                    sx={{
                      gap: 1,
                      minHeight: { xs: 52, sm: "auto" },
                    }}
                  >
                    {loading && <CircularProgress size={18} color="inherit" />}
                    {values.sentVerifyCode
                      ? hasActiveReferral()
                        ? "تایید و ادامه"
                        : "ورود"
                      : "ارسال کد"}
                  </Button>

                  {!values.sentVerifyCode &&
                    !canSendCode &&
                    verifyResendTime > 0 && (
                      <Typography
                        variant="body2"
                        textAlign="center"
                        sx={{
                          mt: 1,
                          color: "text.secondary",
                          fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        }}
                      >
                        لطفاً {verifyResendTime} ثانیه صبر کنید
                      </Typography>
                    )}
                </Grid>

                {hasBothLogin ? (
                  <Grid
                    item
                    xs={12}
                    md={12}
                    container
                    justifyContent={"center"}
                  >
                    <Button
                      variant="text"
                      onClick={() => onChangeLoginKind("UserPass")}
                    >
                      ورود با نام کاربری
                    </Button>
                  </Grid>
                ) : null}
              </Grid>
            </form>
          )}
        </Formik>

        {message && (
          <Alert
            sx={{
              mt: { xs: 1.25, sm: 3 },
              fontSize: { xs: "0.75rem", sm: "0.875rem" },
            }}
            severity={getMessageSeverity(message)}
          >
            {getMessageText(message)}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

// Wrap with error boundary
const MobileLoginWithErrorBoundary = (props) => {
  try {
    return <MobileLogin {...props} />;
  } catch (error) {
    console.error("MobileLogin render error:", error);
    return (
      <Card variant="outlined" className="auth-card-container">
        <CardContent>
          <Box textAlign="center" py={3}>
            <Typography variant="h6" color="error.main" gutterBottom>
              خطایی رخ داده است
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              لطفاً صفحه را رفرش کنید یا دوباره تلاش کنید.
            </Typography>
            <Button
              variant="contained"
              onClick={() => window.location.reload()}
            >
              بارگذاری مجدد
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  }
};

export default MobileLoginWithErrorBoundary;
